import React from 'react'
import { useLocation } from 'react-router-dom'
import { usePageInfo } from 'properties-ui-common'
import { REQUEST_CHANGE_LABEL } from '../MicroFrontEnds/utility/constants'

type Crumb = {
  name: string
  href: string
  __autoGenerated?: boolean
}

function generateBreadcrumbs(pathname: string, searchParams: string): Crumb[] {
  const crumbs: Crumb[] = []
  const search = new URLSearchParams(searchParams)
  const idSuffixes = ['Id', '_id', '']

  const formatSegmentName = (seg: string) => {
    if (!seg) return seg
    let name = seg.replace(/-/g, ' ')
    name = name.replace(/\b\w/g, (char) => char.toUpperCase())
    return name
  }

  const homeCrumb = { name: 'Home', href: '/', __autoGenerated: true }
  const fromCrumbs: Crumb[] = []
  const fromParams: string[] = []

  if (search.has('from')) {
    const fromVals = search.getAll('from')
    fromVals.forEach((val) => fromParams.push(val))
  }

  for (const [key, value] of search.entries()) {
    const match = key.match(/^from(\d+)$/)
    if (match) fromParams.push(value)
  }

  if (fromParams.length > 0) {
    fromParams.forEach((fromValue) => {
      const baseName = formatSegmentName(fromValue)
      const listCrumbUrl = `/${fromValue}`
      fromCrumbs.push({
        name: baseName,
        href: listCrumbUrl,
        __autoGenerated: true,
      })

      const singular = fromValue.endsWith('s')
        ? fromValue.slice(0, -1)
        : fromValue

      const idKey = [...search.keys()].find((k) =>
        idSuffixes.some(
          (suffix) => k.toLowerCase() === (singular + suffix).toLowerCase(),
        ),
      )

      if (idKey) {
        const idVal = search.get(idKey)
        if (idVal) {
          let detailName = baseName
          if (detailName.endsWith('s')) {
            detailName = detailName.slice(0, -1)
          }
          const detailCrumbUrl = `/${fromValue}/${idVal}`
          fromCrumbs.push({
            name: `${detailName} ${idVal}`,
            href: detailCrumbUrl,
            __autoGenerated: true,
          })
        }
      }
    })
  }

  const segments = pathname.split('/').filter(Boolean)
  let cumulativePath = ''
  segments.forEach((segment, idx) => {
    cumulativePath += `/${segment}`
    if (/^\d+$/.test(segment)) {
      const prevCrumb = crumbs[crumbs.length - 1]
      let entityName = prevCrumb ? prevCrumb.name : 'Item'
      if (entityName.endsWith('s')) entityName = entityName.slice(0, -1)
      crumbs.push({
        name: `${entityName} ${segment}`,
        href: cumulativePath,
        __autoGenerated: true,
      })
    } else {
      const formatted =
        segment === 'change-notification-create'
          ? REQUEST_CHANGE_LABEL
          : formatSegmentName(segment)
      crumbs.push({
        name: formatted,
        href: cumulativePath,
        __autoGenerated: true,
      })
    }
  })

  if (search.has('skip_bc_levels')) {
    const skipVals = search?.get?.('skip_bc_levels')?.split?.(',') ?? []
    const skipLevels = skipVals
      .map((val) => parseInt(val, 10))
      .filter((num) => !isNaN(num))
    skipLevels.sort((a, b) => b - a)
    const total = crumbs.length
    for (const level of skipLevels) {
      const index = total - 1 - level
      if (index >= 0 && index < crumbs.length) {
        crumbs.splice(index, 1)
      }
    }
  }

  let finalCrumbs: Crumb[] = [homeCrumb]
  finalCrumbs.push(...fromCrumbs)
  finalCrumbs.push(...crumbs)

  /** 10/15 -  Special case: remove "Cpm" link from auto generated crumbs. 
   This is added for CPM teams consolidation effort since we are prefixing all CPM MFE routes with /cpm 
   so as to manage routes in the consolidated mfe and not the container*/
  if (pathname.startsWith('/cpm/')) {
    finalCrumbs = finalCrumbs.filter((crumb) => crumb.href !== '/cpm')
  }
  return finalCrumbs
}

export function AutoBreadcrumbManager({
  children,
}: React.PropsWithChildren<{}>) {
  const location = useLocation()
  const { breadcrumbs: currentCrumbs, setBreadcrumbs } = usePageInfo()

  React.useEffect(() => {
    const autoCrumbs = generateBreadcrumbs(location.pathname, location.search)
    if (autoCrumbs.length === 0) {
      setBreadcrumbs([])
      return
    }

    const allExistingAutoGenerated =
      currentCrumbs &&
      currentCrumbs.length > 0 &&
      currentCrumbs.every((c: Crumb) => c.__autoGenerated)

    // Check if current breadcrumbs match the current location
    // If the last breadcrumb's href doesn't match current pathname, they're stale
    const lastCrumb = currentCrumbs?.[currentCrumbs.length - 1]
    const breadcrumbsMatchLocation = lastCrumb?.href === location.pathname

    // Only respect manual breadcrumbs if they match the current location
    if (
      !allExistingAutoGenerated &&
      currentCrumbs?.length > 0 &&
      breadcrumbsMatchLocation
    ) {
      return // Respect manually set breadcrumbs for current page
    }
    setBreadcrumbs(autoCrumbs)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search])

  return children
}
